import { MaterialFormulationApi } from '@/domains/material/apis/formulation-api';
import { useFormulationTracking } from '@/composables/formulation/use-formulation-tracking';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { createSharedComposable, useFileDialog } from '@vueuse/core';
import { Toast } from 'patsnap-biz';
import { setupI18n } from '@eureka/shared/locales/i18n';
import { computed, ref, watch } from 'vue';

const UPLOAD_STORAGE_KEY = 'mse-formulation-upload-files';

export type StoredFileInputItem = {
  name: string;
  context_id: string;
  type: string;
  extension: string;
};

type FileObject = {
  file: File | null;
  id: number | string;
  name: string;
  size: string;
  isUploading: boolean;
  finishedUploading: boolean;
  context_id: string;
  type: string;
  extension: string;
};

function loadStoredFiles(): FileObject[] {
  try {
    const raw = sessionStorage.getItem(UPLOAD_STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as StoredFileInputItem[];
    if (!Array.isArray(arr) || arr.length === 0) return [];
    return arr.map((item, index) => ({
      file: null,
      id: `stored-${index}-${Date.now()}`,
      name: item.name,
      size: '',
      isUploading: false,
      finishedUploading: true,
      context_id: item.context_id,
      type: item.type || 'excel',
      extension: item.extension || '',
    }));
  } catch {
    return [];
  }
}

function saveStoredFiles(files: FileObject[]) {
  const toStore = files
    .filter((f) => f.finishedUploading && f.context_id)
    .map((f) => ({ name: f.name, context_id: f.context_id, type: f.type, extension: f.extension }));
  if (toStore.length === 0) {
    sessionStorage.removeItem(UPLOAD_STORAGE_KEY);
  } else {
    sessionStorage.setItem(UPLOAD_STORAGE_KEY, JSON.stringify(toStore));
  }
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  const size = bytes / Math.pow(k, i);

  const decimals = i <= 1 ? 0 : 1;

  return `${size.toFixed(decimals)}${units[i]}`;
}

export const useFormulationUpload = createSharedComposable(() => {
  const formulationApiService = injectService<MaterialFormulationApi>(MaterialFormulationApi);
  const { trackFormulation } = useFormulationTracking();
  const { t } = setupI18n();
  const stored = loadStoredFiles();
  const selectedFiles = ref<FileObject[]>(stored);
  const id = ref(stored.length);

  const { files, open, reset } = useFileDialog({
    multiple: false,
    accept: '.xlsx,.xls,.csv',
  });

  const upload = async (myFile: FileObject) => {
    const index = selectedFiles.value.findIndex((f) => f.id === myFile.id);
    if (index === -1) return;

    selectedFiles.value[index] = { ...selectedFiles.value[index], isUploading: true };
    try {
      if (!myFile.file) {
        return;
      }
      const res = await formulationApiService.uploadUserContext(myFile.file);

      selectedFiles.value[index] = {
        ...selectedFiles.value[index],
        isUploading: false,
        finishedUploading: true,
        context_id: res.id,
      };
      void trackFormulation('mse_formulation_upload_file', {
        upload_status: 'completed',
      });
    } catch {
      selectedFiles.value.splice(index, 1);
      reset();
      Toast({
        message: t('Formulation.uploadError'),
        type: 'error',
        duration: 5000,
        showClose: true,
      });
      void trackFormulation('mse_formulation_upload_file', {
        upload_status: 'failed',
      });
    }
  };

  watch(files, (fileList) => {
    if (!fileList?.length) return;

    Array.from(fileList).forEach((f) => {
      const newFile = {
        file: f,
        id: id.value,
        name: f.name,
        size: formatFileSize(f.size),
        isUploading: false,
        finishedUploading: false,
        context_id: '',
        type: 'excel',
        extension: f.type,
      };
      selectedFiles.value.push(newFile);
      upload(newFile);
      id.value++;
    });
  });

  const removeFile = (id: number | string) => {
    reset();
    const index = selectedFiles.value.findIndex((f) => f.id === id);

    if (index !== -1) {
      selectedFiles.value.splice(index, 1);
    }
  };

  const allFinishedUploading = computed(() => {
    return selectedFiles.value.every((f) => f.finishedUploading);
  });

  const context_id = computed(() => {
    const finishedFile = selectedFiles.value.find((f) => f.finishedUploading && f.context_id);
    return finishedFile?.context_id || '';
  });

  watch(selectedFiles, () => saveStoredFiles(selectedFiles.value), { deep: true });

  const openDialog = () => {
    if (selectedFiles.value.length < 1) {
      open();
    }
  };

  const clearAllUploads = () => {
    selectedFiles.value = [];
    id.value = 0;
    reset();
    sessionStorage.removeItem(UPLOAD_STORAGE_KEY);
  };

  return {
    openDialog,
    removeFile,
    selectedFiles,
    allFinishedUploading,
    context_id,
    clearAllUploads,
  };
});
